/**
 * The live log of a verify or deploy job (job:<id>): the hub streams it from the machine as
 * SSE `log` chunks into the store. Demo mode writes plausible lines into the same slot.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Id } from '@orgus/protocol';
import { openLog } from '../../lib/api.ts';
import { useOrgus, useLog } from '../../lib/store.ts';

export interface JobLog {
  text: string;
  live: boolean;
  loading: boolean;
  error: unknown;
  retry: () => void;
}

function mockLines(command: string, failed: boolean): string[] {
  const out = [`$ ${command}`, 'Installing from the lockfile (offline cache)', 'Typecheck: 6 packages', 'Typecheck passed in 11.2 s', 'Lint passed'];
  if (command.includes('deploy')) return [`$ ${command}`, 'Build output ready', 'Uploading 14 files', 'Health check passed', 'Exited with code 0.'];
  if (failed) return [...out, 'Unit tests: 212 passed, 1 failed', 'FAIL apps/web/src/pages/Audit.test.ts > exports every column', 'error: expected 9 columns, got 8', 'Exited with code 1.'];
  return [...out, 'Unit tests: 384 passed in 31.8 s', 'Exited with code 0.'];
}

/** Open (and close) the log stream of a job while `enabled`. */
export function useJobLog(jobId: Id | null, enabled: boolean, demo: { command: string; failed: boolean; running: boolean }): JobLog {
  const mode = useOrgus((s) => s.mode);
  const [streamId, setStreamId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const log = useLog(streamId);

  useEffect(() => {
    if (!enabled || !jobId) return;
    let cancelled = false;
    let opened: string | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    setError(null);
    if (mode === 'mock') {
      const id = `mock-job:${jobId}`;
      opened = id;
      setStreamId(id);
      const st = useOrgus.getState();
      st.clearLog(id);
      // The demo stream is this tab's own, like an opened one: without this its lines sit unseen in earlyLogs.
      st.trackLog(id);
      const lines = mockLines(demo.command, demo.failed);
      const upto = demo.running ? lines.length - 1 : lines.length;
      st.appendLog({ streamId: id, ref: { type: 'job', id: jobId }, text: `${lines.slice(0, upto).join('\n')}\n`, end: !demo.running });
      if (demo.running) {
        let n = 0;
        timer = setInterval(() => {
          n++;
          useOrgus.getState().appendLog({ streamId: id, ref: { type: 'job', id: jobId }, text: `  spec ${n}: ok (${(0.2 + (n % 7) / 10).toFixed(2)} s)\n` });
        }, 900);
      }
    } else {
      setLoading(true);
      openLog({ type: 'job', id: jobId })
        .then(({ streamId: id }) => {
          if (cancelled) {
            useOrgus.getState().clearLog(id);
            return;
          }
          opened = id;
          setStreamId(id);
        })
        .catch((e: unknown) => {
          if (!cancelled) setError(e);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (opened) useOrgus.getState().clearLog(opened);
      setStreamId(null);
    };
    // The stream follows the job's identity, not every change to the landing.
  }, [enabled, jobId, mode, attempt, demo.running]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { text: log?.text ?? '', live: !!streamId && !log?.ended && demo.running, loading, error, retry };
}
